// Sticky-bottom audio player for ConversationDetailPage.
//
// Streams the assembled WAV from
//   GET /conversations/{id}/audio
// (HTTP Range supported — the browser <audio> element + WaveSurfer
// rely on Range for seek). On mount we also fetch the manifest from
//   GET /conversations/{id}/audio/manifest
// to know total duration up-front and to expose per-turn Prev/Next jumps.
//
// The player publishes a `currentTimeMs` value via the `onTimeChange`
// prop; the parent page hoists that state and feeds it to the
// TaggedTranscriptView / CleanedTranscriptView for word/turn-level
// highlighting + auto-scroll.
//
// Click-to-seek: the parent passes a `seekRef` ref; this component
// writes a `{ seek(ms) }` API onto it so transcript word clicks can
// drive the player directly.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Text,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import {
  getConversationAudioManifest,
  getConversationAudioPeaks,
  getConversationAudioUrl,
} from "../services/embed";

// ~6 muted region colors cycled through so adjacent snippet regions
// stay visually distinct on the waveform.
const REGION_COLORS = [
  "rgba(99, 102, 241, 0.15)",   // indigo
  "rgba(16, 185, 129, 0.15)",   // emerald
  "rgba(245, 158, 11, 0.15)",   // amber
  "rgba(236, 72, 153, 0.15)",   // pink
  "rgba(20, 184, 166, 0.15)",   // teal
  "rgba(139, 92, 246, 0.15)",   // purple
];

// Local play/pause/skip icons — Chakra ships `ArrowUpIcon` / `ArrowDownIcon`
// not a triangle. Inline SVGs keep the player free of new dependencies.
const PlayGlyph = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
    <polygon points="3,2 13,8 3,14" fill="currentColor" />
  </svg>
);
const PauseGlyph = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
    <rect x="3" y="2" width="3.5" height="12" fill="currentColor" />
    <rect x="9.5" y="2" width="3.5" height="12" fill="currentColor" />
  </svg>
);
const PrevGlyph = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
    <rect x="3" y="3" width="2" height="10" fill="currentColor" />
    <polygon points="14,3 7,8 14,13" fill="currentColor" />
  </svg>
);
const NextGlyph = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
    <polygon points="2,3 9,8 2,13" fill="currentColor" />
    <rect x="11" y="3" width="2" height="10" fill="currentColor" />
  </svg>
);

function formatTime(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "0:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const ConversationAudioPlayer = ({
  conversationId,
  // Turn boundaries the Prev/Next buttons jump between. Each is `start_ms`.
  // Optional — when empty, the buttons are disabled.
  turnStartsMs = [],
  // Notified with the current playback position whenever it changes.
  onTimeChange,
  // Notified once the manifest has loaded — used by the parent to learn
  // `audio_start_epoch_ms` so it can normalize the epoch-flavored
  // word/turn timestamps in TAGGED `data.turns` / CLEANED `turns_meta`
  // into the same conv-relative frame as `currentTimeMs`.
  onManifestLoaded,
  // The parent passes a ref; we write `{ seek(ms) }` onto it so the
  // transcript view can drive the player when a word is clicked.
  seekRef,
  // Optional list of `{ snippet_id, start, end }` (seconds, conv-relative)
  // boundaries. When non-empty, each is painted as a colored region on
  // the waveform. Cycles through a muted palette so adjacent snippets
  // stay distinct. Computed by the parent from the RAW word stream
  // (see `collectSnippetBoundaries` in RawTranscriptView).
  snippetBoundaries = [],
  // Test seam — production passes `undefined`; tests pass a fake factory
  // returning a fake WaveSurfer class. Same pattern as RunTestAudioDialog.
  wavesurferFactory,
  // Test seam — production passes `undefined` and we lazy-import the
  // regions plugin from wavesurfer.js. Tests pass a fake factory.
  regionsPluginFactory,
  // PROFILE/embed seam — production + desktop pass `undefined` and the
  // player fetches the manifest + peaks from the backend and streams the
  // assembled WAV from `/audio`. The EmbedConversationViewer's `?profile=1`
  // mode passes synthetic `manifestOverride` + `peaksOverride` and an
  // `audioUrlOverride` pointing at a looped local sample, so the memory
  // profile can run without a real assembled-WAV endpoint. When provided,
  // these short-circuit the network fetch but leave every downstream code
  // path (peaks → WaveSurfer, ranged MediaElement playback) identical.
  manifestOverride,
  peaksOverride,
  audioUrlOverride,
  // LIVE/embed seam (M3) — production desktop + the static viewer pass
  // `undefined`. The EmbedConversationViewer passes this for an IN-PROGRESS
  // conversation whose audio is still growing: `{ durationMs, peaks }` where
  // `peaks` is the SAME `{peaks:[[min,max]...], max_value}` shape the peaks
  // endpoint returns. When it changes we extend the EXISTING WaveSurfer
  // instance via `setOptions({ peaks, duration })` — NON-destructively, so
  // the waveform + playback head grow without tearing down + re-fetching
  // (which would interrupt playback and reset the head). The initial
  // manifest/peaks still come from the player's own fetch (unchanged); this
  // only carries subsequent growth. Never passed by the desktop → additive.
  liveExtend,
}) => {
  const waveContainerRef = useRef(null);
  const waveSurferRef = useRef(null);
  const regionsPluginRef = useRef(null);
  const audioUrlRef = useRef(null);
  // requestAnimationFrame handle for the high-resolution time pulse.
  // WaveSurfer's `timeupdate` event piggybacks on HTMLAudioElement, which
  // fires only ~4 times per second. That cadence is too coarse for word-
  // level highlighting (words < 250ms can fall entirely between ticks).
  // While playing, we drive a rAF loop that polls `getCurrentTime` and
  // pushes the value through the same setter the timeupdate handler uses.
  const rafHandleRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [durationMs, setDurationMs] = useState(0);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [peaksData, setPeaksData] = useState(null);

  // Latest onTimeChange handler via ref — keeps the WaveSurfer
  // timeupdate subscription from forcing a re-init when the parent
  // passes a fresh callback each render.
  const onTimeChangeRef = useRef(onTimeChange);
  useEffect(() => {
    onTimeChangeRef.current = onTimeChange;
  }, [onTimeChange]);

  // Fetch the manifest first so we know total_duration_ms before the
  // first audio byte lands. WaveSurfer will also produce a duration once
  // it has the WAV header, but the manifest is authoritative and the
  // player can show it immediately.
  useEffect(() => {
    let cancelled = false;
    if (!conversationId) return undefined;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        // Profile/embed short-circuit: when synthetic overrides are passed,
        // skip the network entirely and feed the same shapes downstream.
        const manifestPromise =
          manifestOverride != null
            ? Promise.resolve(manifestOverride)
            : getConversationAudioManifest(conversationId);
        const peaksPromise =
          peaksOverride != null
            ? Promise.resolve(peaksOverride)
            : getConversationAudioPeaks(conversationId, 1500).catch(() => null);
        const [manifest, peaks] = await Promise.all([
          manifestPromise,
          peaksPromise,
        ]);
        if (cancelled) return;
        if (manifest?.total_duration_ms != null) {
          setDurationMs(Number(manifest.total_duration_ms) || 0);
        }
        if (peaks?.peaks && Array.isArray(peaks.peaks)) {
          setPeaksData(peaks);
        }
        if (typeof onManifestLoaded === "function") {
          onManifestLoaded(manifest);
        }
      } catch (e) {
        if (cancelled) return;
        const detail =
          e?.response?.data?.detail ||
          e?.message ||
          "Audio manifest failed to load.";
        setError(detail);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, manifestOverride, peaksOverride]);

  // Stable snapshot of snippetBoundaries so re-renders with the SAME
  // boundary set don't reinitialize WaveSurfer. (Default-prop `[]` is a
  // fresh array each render — using it directly as a dep tears the
  // player down on every render.)
  const boundariesKey = useMemo(() => {
    if (!Array.isArray(snippetBoundaries) || snippetBoundaries.length === 0) {
      return "";
    }
    return snippetBoundaries
      .map(
        (b) =>
          `${b?.snippet_id ?? ""}:${Number(b?.start) || 0}-${Number(b?.end) || 0}`
      )
      .join("|");
  }, [snippetBoundaries]);

  // Initialize WaveSurfer once we have a container + a URL. Lazily
  // import wavesurfer.js so jsdom never executes the WebAudio path.
  //
  // We construct WaveSurfer with pre-computed peaks + an explicit
  // duration, then set `preload="metadata"` on the underlying <audio>
  // element so the browser fetches just enough to satisfy seek without
  // slurping the whole 50-100 MB file just to render the waveform. The
  // actual audio bytes come back via Range requests on play / drag.
  useEffect(() => {
    if (!conversationId || !waveContainerRef.current) return undefined;
    // Wait until we have peaks AND a duration before initializing — these
    // two together let WaveSurfer skip the full-file pre-fetch.
    if (!peaksData || !durationMs) return undefined;

    let disposed = false;
    let ws = null;

    audioUrlRef.current =
      audioUrlOverride || getConversationAudioUrl(conversationId);

    (async () => {
      try {
        const factory =
          wavesurferFactory ||
          (async () => (await import("wavesurfer.js")).default);
        const WaveSurfer = await factory();
        if (disposed || !waveContainerRef.current) return;

        // Optionally load the regions plugin — production lazy-imports
        // it; tests pass a fake factory.
        let regionsPlugin = null;
        const wantRegions =
          Array.isArray(snippetBoundaries) && snippetBoundaries.length > 0;
        if (wantRegions) {
          try {
            const RegionsFactory =
              regionsPluginFactory ||
              (async () =>
                (await import("wavesurfer.js/dist/plugins/regions.js"))
                  .default);
            const RegionsCtor = await RegionsFactory();
            regionsPlugin = RegionsCtor?.create
              ? RegionsCtor.create()
              : null;
          } catch (_e) {
            regionsPlugin = null;
          }
        }

        // WaveSurfer v7's `peaks` option expects a SINGLE Float32Array
        // of normalized [-1, 1] samples (or a 2-element array of those
        // for stereo). It does NOT understand `[[min, max], ...]` pairs
        // directly — passing pairs renders an empty (flat) waveform,
        // which is the bug app#23 shipped. The backend's peaks endpoint
        // returns `[[min_int16, max_int16], ...]` so we flatten to the
        // ABS-max amplitude per bin (whichever of |min|, |max| is
        // larger) and normalize against `max_value` (default 32767 for
        // raw int16). Using abs-max preserves visual amplitude in both
        // halves of the waveform — WaveSurfer mirrors a single channel
        // around the centerline, so a positive normalized value is
        // sufficient to draw both the top and bottom of the bin.
        const max = Number(peaksData.max_value) || 32767;
        const bins = Array.isArray(peaksData.peaks) ? peaksData.peaks : [];
        const normalizedPeaks = new Float32Array(bins.length);
        for (let i = 0; i < bins.length; i += 1) {
          const pair = bins[i];
          const lo = Number(pair?.[0]) || 0;
          const hi = Number(pair?.[1]) || 0;
          const amp = Math.max(Math.abs(lo), Math.abs(hi)) / max;
          // Clamp to [0, 1] — backend should already be in range but
          // a stray spike shouldn't blow up the waveform layout.
          normalizedPeaks[i] = amp > 1 ? 1 : amp;
        }

        const createOpts = {
          container: waveContainerRef.current,
          waveColor: "#cbd5e0",
          progressColor: "#3182ce",
          cursorColor: "#2b6cb0",
          height: 48,
          url: audioUrlRef.current,
          // Keep MediaElement backend so Range / partial-content seeks
          // work via the native browser <audio> element.
          backend: "MediaElement",
          peaks: normalizedPeaks,
          duration: durationMs / 1000,
          mediaControls: false,
        };
        if (regionsPlugin) createOpts.plugins = [regionsPlugin];

        ws = WaveSurfer.create(createOpts);
        regionsPluginRef.current = regionsPlugin;

        // Tell the browser to fetch only metadata up-front — the
        // <audio> element will then Range-request real bytes on play /
        // seek. Without this, MediaElement defaults to `preload="auto"`
        // and downloads the whole assembled WAV.
        try {
          const media = ws.getMediaElement?.();
          if (media) media.preload = "metadata";
        } catch {
          /* noop */
        }

        // Once ready, paint snippet regions if we have them.
        ws.on("ready", () => {
          if (disposed) return;
          const d = ws.getDuration?.();
          if (Number.isFinite(d) && d > 0) {
            setDurationMs(Math.round(d * 1000));
          }
          if (regionsPlugin && Array.isArray(snippetBoundaries)) {
            for (let i = 0; i < snippetBoundaries.length; i += 1) {
              const b = snippetBoundaries[i];
              if (
                !Number.isFinite(b?.start) ||
                !Number.isFinite(b?.end) ||
                b.end <= b.start
              ) {
                continue;
              }
              try {
                regionsPlugin.addRegion({
                  id: `snippet-${b.snippet_id ?? i}`,
                  start: b.start,
                  end: b.end,
                  color: REGION_COLORS[i % REGION_COLORS.length],
                  drag: false,
                  resize: false,
                });
              } catch {
                /* noop — region plugin variant mismatches are non-fatal */
              }
            }
          }
        });
        // Push a high-resolution time sample. Called both from the
        // browser's `timeupdate` event (low cadence ~4Hz) and from a
        // rAF poll loop while playing (~60Hz). Re-rendering every frame
        // is cheap — `setCurrentTimeMs` only flushes when the rounded
        // ms value actually changes (React bails on identical state).
        const pushTime = (currentTimeSeconds) => {
          if (!Number.isFinite(currentTimeSeconds)) return;
          const ms = Math.round(currentTimeSeconds * 1000);
          setCurrentTimeMs(ms);
          const cb = onTimeChangeRef.current;
          if (typeof cb === "function") cb(ms);
        };

        // rAF loop — drains `ws.getCurrentTime()` at the browser's
        // display refresh rate while playing. Stopped on pause/finish
        // and on teardown via cancelAnimationFrame.
        const stopRaf = () => {
          if (rafHandleRef.current) {
            if (typeof cancelAnimationFrame === "function") {
              cancelAnimationFrame(rafHandleRef.current);
            }
            rafHandleRef.current = 0;
          }
        };
        const startRaf = () => {
          if (typeof requestAnimationFrame !== "function") return;
          stopRaf();
          const tick = () => {
            if (disposed) return;
            const w = waveSurferRef.current;
            if (!w) return;
            try {
              const t = w.getCurrentTime?.();
              if (Number.isFinite(t)) pushTime(t);
            } catch {
              /* noop */
            }
            rafHandleRef.current = requestAnimationFrame(tick);
          };
          rafHandleRef.current = requestAnimationFrame(tick);
        };

        ws.on("play", () => {
          setIsPlaying(true);
          startRaf();
        });
        ws.on("pause", () => {
          setIsPlaying(false);
          stopRaf();
        });
        ws.on("finish", () => {
          setIsPlaying(false);
          stopRaf();
        });
        // Keep the timeupdate subscription as a safety net (covers the
        // case where rAF is throttled in a backgrounded tab — the
        // browser still ticks timeupdate every ~250ms there).
        ws.on("timeupdate", (currentTime) => pushTime(currentTime));
        // Seek events fire when the user clicks the waveform / a word
        // jumps the player — refresh the highlight immediately even
        // when paused.
        ws.on("seeking", (currentTime) => pushTime(currentTime));
        waveSurferRef.current = ws;
      } catch (e) {
        if (!disposed) {
          setError("Audio player failed to initialise.");
        }
      }
    })();

    return () => {
      disposed = true;
      if (rafHandleRef.current) {
        if (typeof cancelAnimationFrame === "function") {
          cancelAnimationFrame(rafHandleRef.current);
        }
        rafHandleRef.current = 0;
      }
      try {
        ws?.destroy?.();
      } catch {
        /* noop */
      }
      waveSurferRef.current = null;
      regionsPluginRef.current = null;
    };
    // onTimeChange / wavesurferFactory rarely change after mount but
    // we include them so a parent that swaps callbacks does refresh.
  }, [
    conversationId,
    wavesurferFactory,
    peaksData,
    durationMs,
    boundariesKey,
    regionsPluginFactory,
    audioUrlOverride,
  ]);

  // Imperative API exposed to the parent via `seekRef`. Lets a transcript
  // word click drive the player. Stored on `.current` so re-renders don't
  // tear down references the parent already grabbed.
  useEffect(() => {
    if (!seekRef) return undefined;
    seekRef.current = {
      seek(ms) {
        const ws = waveSurferRef.current;
        if (!ws) return;
        const totalS = ws.getDuration?.() || (durationMs / 1000 || 0);
        if (!Number.isFinite(totalS) || totalS <= 0) return;
        const target = Math.min(Math.max(Number(ms) || 0, 0), totalS * 1000) / 1000;
        try {
          ws.setTime?.(target);
        } catch {
          /* noop */
        }
      },
    };
    return () => {
      if (seekRef && seekRef.current) seekRef.current = null;
    };
  }, [seekRef, durationMs]);

  // LIVE EXTEND (M3) — when a growing conversation reports new peaks +
  // duration, push them into the already-mounted WaveSurfer WITHOUT a
  // teardown. We reuse the EXACT same int16-pairs → normalized Float32Array
  // collapse the init path uses, then `setOptions({ peaks, duration })`.
  // WaveSurfer v7 redraws from the new peaks + extends its timeline in place,
  // so the playback head stays where it is and playback isn't interrupted.
  // We also bump `durationMs` state so the "total time" label grows.
  //
  // Keyed by content (bins length + duration) so an identical re-emit is a
  // no-op and we never thrash setOptions. Guarded by optional chaining so an
  // older WaveSurfer without setOptions degrades to "waveform catches up on
  // the next natural init" rather than throwing.
  const liveExtendBins = Array.isArray(liveExtend?.peaks?.peaks)
    ? liveExtend.peaks.peaks.length
    : 0;
  const liveExtendDur = Number(liveExtend?.durationMs) || 0;
  // Non-empty whenever EITHER peaks OR a positive duration is present, so a
  // duration-only growth (no fresh peaks yet) still extends the timeline.
  const liveExtendKey =
    liveExtend && (liveExtendBins > 0 || liveExtendDur > 0)
      ? `${liveExtendBins}:${liveExtendDur}`
      : "";
  const lastLiveExtendKeyRef = useRef("");
  useEffect(() => {
    if (!liveExtend) return;
    if (!liveExtendKey || liveExtendKey === lastLiveExtendKeyRef.current) return;
    const ws = waveSurferRef.current;

    const nextDurationMs = Number(liveExtend.durationMs);
    if (Number.isFinite(nextDurationMs) && nextDurationMs > 0) {
      // Only ever grow — never shrink the timeline out from under the head.
      setDurationMs((prev) => (nextDurationMs > prev ? nextDurationMs : prev));
    }

    const peaksPayload = liveExtend.peaks;
    if (ws && peaksPayload && Array.isArray(peaksPayload.peaks)) {
      const max = Number(peaksPayload.max_value) || 32767;
      const bins = peaksPayload.peaks;
      const normalizedPeaks = new Float32Array(bins.length);
      for (let i = 0; i < bins.length; i += 1) {
        const pair = bins[i];
        const lo = Number(pair?.[0]) || 0;
        const hi = Number(pair?.[1]) || 0;
        const amp = Math.max(Math.abs(lo), Math.abs(hi)) / max;
        normalizedPeaks[i] = amp > 1 ? 1 : amp;
      }
      try {
        const opts = { peaks: normalizedPeaks };
        if (Number.isFinite(nextDurationMs) && nextDurationMs > 0) {
          opts.duration = nextDurationMs / 1000;
        }
        ws.setOptions?.(opts);
      } catch {
        /* noop — non-destructive extend is best-effort */
      }
    }
    lastLiveExtendKeyRef.current = liveExtendKey;
  }, [liveExtend, liveExtendKey]);

  const togglePlay = () => {
    const ws = waveSurferRef.current;
    if (!ws) return;
    try {
      if (ws.isPlaying?.()) ws.pause();
      else ws.play();
    } catch {
      /* noop */
    }
  };

  // Prev/Next jumps between turn boundaries.
  const jumpToTurn = (direction) => {
    if (!Array.isArray(turnStartsMs) || turnStartsMs.length === 0) return;
    // Find the index of the turn whose start matches or precedes current.
    let idx = turnStartsMs.findIndex((t) => t > currentTimeMs);
    if (idx === -1) idx = turnStartsMs.length;
    // idx points at the next-future turn. Current turn index = idx - 1.
    const target =
      direction === "next"
        ? turnStartsMs[Math.min(idx, turnStartsMs.length - 1)]
        : turnStartsMs[Math.max(idx - 2, 0)];
    if (typeof target === "number") {
      seekRef?.current?.seek?.(target);
    }
  };

  return (
    <Box
      position="sticky"
      bottom={0}
      left={0}
      right={0}
      bg="white"
      borderTopWidth="1px"
      borderColor="gray.200"
      px={4}
      py={2}
      zIndex={5}
      boxShadow="0 -2px 6px rgba(0,0,0,0.04)"
      data-testid="conversation-audio-player"
    >
      <Flex align="center" gap={3}>
        <HStack spacing={1}>
          <Tooltip label="Previous turn">
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Previous turn"
              icon={<PrevGlyph />}
              isDisabled={!turnStartsMs || turnStartsMs.length === 0}
              onClick={() => jumpToTurn("prev")}
              data-testid="audio-player-prev-turn"
            />
          </Tooltip>
          <Tooltip label={isPlaying ? "Pause" : "Play"}>
            <IconButton
              size="sm"
              colorScheme="blue"
              aria-label={isPlaying ? "Pause" : "Play"}
              icon={isPlaying ? <PauseGlyph /> : <PlayGlyph />}
              onClick={togglePlay}
              isDisabled={isLoading || !!error}
              data-testid="audio-player-play"
            />
          </Tooltip>
          <Tooltip label="Next turn">
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Next turn"
              icon={<NextGlyph />}
              isDisabled={!turnStartsMs || turnStartsMs.length === 0}
              onClick={() => jumpToTurn("next")}
              data-testid="audio-player-next-turn"
            />
          </Tooltip>
        </HStack>

        <Box flex={1} minW={0}>
          {error ? (
            <Text fontSize="xs" color="red.500" data-testid="audio-player-error">
              {error}
            </Text>
          ) : (
            <Box
              ref={waveContainerRef}
              data-testid="audio-player-waveform"
              w="100%"
            />
          )}
        </Box>

        <HStack spacing={1} fontSize="xs" color="gray.600" fontFamily="mono">
          <Text data-testid="audio-player-current-time">
            {formatTime(currentTimeMs)}
          </Text>
          <Text>/</Text>
          <Text data-testid="audio-player-total-time">
            {formatTime(durationMs)}
          </Text>
          {isLoading && <Spinner size="xs" ml={2} />}
        </HStack>
      </Flex>
    </Box>
  );
};

export default ConversationAudioPlayer;
